import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {z} from 'zod';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {Placement_UI} from 'common/src/modules/campaignPlacements/placements.types';
import formatAdSizeToString from 'common/src/modules/adSize/utils/formatAdSizeToString';
import {
  PlacementCustomParamValue,
  placementCustomParamValueToName,
} from 'common/src/modules/campaigns/campaigns.types';

const getPlacementsToolSchema = z.object({});

function formatPlacement(placement: Placement_UI): string {
  const textParams = placement.customTextParams
    .map(
      (param) =>
        `${placementCustomParamValueToName[param.type as PlacementCustomParamValue]}: ${param.value}`,
    )
    .join(', ');
  const dateParams = placement.customDateTimeParams
    .map(
      (param) =>
        `${placementCustomParamValueToName[param.type as PlacementCustomParamValue]}: ${param.value.toISO()}`,
    )
    .join(', ');
  const booleanParams = placement.customBooleanParams
    .map(
      (param) =>
        `${placementCustomParamValueToName[param.type as PlacementCustomParamValue]}: ${param.value.toString()}`,
    )
    .join(', ');

  return `
- id: ${placement.id}
- plcId: ${placement.placementId}
- dspId: ${placement.dspId}
- name: ${placement.name}
- adSize: ${formatAdSizeToString(placement.adSize)},
- environment: ${placement.environment}
- custom text parameters: ${textParams}
- custom date parameters: ${dateParams}
- custom boolean parameters: ${booleanParams}`.trim();
}

export default function getPlacementsTool(store: CLStore) {
  async function execute(_params: ToolExecuteParams<typeof getPlacementsToolSchema>) {
    const state = store.getState();
    const campaignId = state.campaignPage.campaignTree.campaign?.id;
    const campaignVersionId = state.campaignPage.campaignTree.campaignVersions[0]?.id;
    const placements = state.campaignPage.campaignTree.campaignPlacements;

    if (!campaignId || !campaignVersionId) {
      return toolResult('Campaign or campaign version not found');
    }

    if (placements.length === 0) {
      return toolResult(
        `No placements found in the current campaign version (campaign ${campaignId}, version ${campaignVersionId})`,
      );
    }

    const formattedPlacements = placements.map((placement, index) => {
      return `Placement ${index + 1}:\n${formatPlacement(placement)}`;
    });

    return toolResult(
      `Found ${placements.length} placement${placements.length > 1 ? 's' : ''} in campaign ${campaignId}, version ${campaignVersionId}:\n\n${formattedPlacements.join('\n\n')}`,
    );
  }

  return tool({
    name: 'getPlacements',
    displayName: 'Getting placements',
    description:
      'Get all placements from the current campaign version. Returns full details for each placement.',
    inputSchema: getPlacementsToolSchema,
    execute,
  });
}
